import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Detail } from '@/types/detail';
import { ConfigurationType } from '@/types/metadata/metadata';
import { findSubroutine } from '@/util/application';
import { padToArity } from '@/util/arity';
import { findEntrySubroutine } from '@/util/blocks';
import { isString } from '@/util/typeGuards';
import { returnvaluesUtil } from '@/util/validation/returnvalues';
import { Card, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { ParamProps } from './ParamRenderer';
import ParamStringInteger from './ParamStringInteger';

const ParamReturnValues: FC<ParamProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();
  if (!props.parentId) return <></>;

  const initialValue = returnvaluesUtil().getValue(props.configuration, props.initialValue) || '';
  // An appreturnvalues list returns from the whole application, so its rows come from the entry subroutine.
  const returnTypes = (
    props.configuration.type === 'appreturnvalues'
      ? findEntrySubroutine(application.subroutines, application.blocks.nodes, metadata)
      : findSubroutine(application.subroutines, props.parentId)
  )?.return_types;

  return (
    <Card variant='outlined'>
      <Stack gap={1} sx={{ minHeight: '4rem', p: 1 }}>
        <List subheader={props.configuration.label} />
        {!returnTypes?.length ? (
          <Typography variant='body1' fontStyle='italic' color='text.disabled' p={2} mt={-2}>
            No {props.configuration.label.toLocaleLowerCase()} available
          </Typography>
        ) : (
          returnTypes.map((returnType, index) => {
            const type = isString(returnType) ? returnType : returnType.type;
            return (
              <ParamStringInteger
                nodeId={props.nodeId}
                key={`return_types.${index}`}
                parentId={props.parentId}
                configuration={{
                  ...props.configuration,
                  type: type as ConfigurationType,
                  subtype: type,
                  required: props.configuration.required,
                  label: returnType.label || `${props.configuration.label} ${index + 1}`
                }}
                initialValue={initialValue && initialValue[index]}
                onChange={(...args) => {
                  const newInitialValue = [...(initialValue || [])];
                  args.forEach(([, newValue]) => (newInitialValue[index] = newValue as Detail));
                  props.onChange([props.configuration.value, padToArity(newInitialValue, returnTypes.length)]);
                }}
                errors={props.errors}
              />
            );
          })
        )}
      </Stack>
    </Card>
  );
};

export default ParamReturnValues;
